import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useCategoriesStore } from '@/store/categoriesStore';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { 
  Search, 
  Plus, 
  MoreVertical, 
  Eye, 
  Download, 
  Trash2, 
  Edit, 
  BarChart3,
  PieChart,
  TrendingUp,
  Star
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { format } from 'date-fns';
import { SavedReport } from '../types';
import { Chart } from './Chart';
import { toast } from 'sonner';
import { MetricCard } from './MetricCard';
import { AnalyticsPeriod } from '../types';
import { generatePDFReport, generateExcelReport } from '@/lib/reportGenerator';
import type { Report } from '@/types/analytics';
import * as XLSX from 'xlsx-js-style';

interface SavedReportsProps {
  onCreateNew: () => void;
  transactions?: any[];
  selectedPeriod?: AnalyticsPeriod;
  mainCurrencySymbol?: string;
  convertToDefault?: (amount: number, fromCurrency: string) => number;
  accounts?: any[];
  defaultCurrency?: string;
}

// Mock data for demonstration - replace with actual API calls
const MOCK_REPORTS: SavedReport[] = [
  {
    id: '1',
    name: 'Monthly Expense Breakdown',
    description: 'Category-wise breakdown of expenses for the current month',
    type: 'category_breakdown',
    chartType: 'pie',
    period: {
      label: 'This Month',
      value: 'thisMonth',
      startDate: new Date(2024, 11, 1),
      endDate: new Date(2024, 11, 31)
    },
    filters: {
      transactionTypes: ['income', 'expense'],
      categories: [],
      accounts: [],
      tags: [],
      minAmount: undefined,
      maxAmount: undefined
    },
    includeMetrics: ['totalIncome', 'totalExpenses', 'netSavings'],
    categories: [],
    createdAt: new Date(2024, 11, 1),
    updatedAt: new Date(2024, 11, 15),
    data: {
      labels: [],
      datasets: []
    },
    metrics: {
      totalIncome: 5000,
      totalExpenses: 3200,
      netSavings: 1800,
      savingsRate: 36,
      transactionCount: 45,
      avgTransactionAmount: 120,
      avgDailySpending: 103,
      topCategory: { name: 'Food', amount: 800, percentage: 25 },
      monthOverMonth: { income: 5, expenses: -2, savings: 15 }
    },
    lastRun: new Date(2024, 11, 15),
    isFavorite: true
  },
  {
    id: '2',
    name: 'Income vs Expenses Trend',
    description: 'Track income and expenses over the last 6 months',
    type: 'trend_analysis',
    chartType: 'line',
    period: {
      label: 'Last 6 Months',
      value: 'last6Months',
      startDate: new Date(2024, 5, 1),
      endDate: new Date(2024, 11, 31)
    },
    filters: {
      transactionTypes: ['income', 'expense'],
      categories: [],
      accounts: [],
      tags: [],
      minAmount: undefined,
      maxAmount: undefined
    },
    includeMetrics: ['totalIncome', 'totalExpenses', 'netSavings'],
    categories: [],
    createdAt: new Date(2024, 10, 15),
    updatedAt: new Date(2024, 11, 10),
    data: {
      labels: [],
      datasets: []
    },
    metrics: {
      totalIncome: 30000,
      totalExpenses: 22000,
      netSavings: 8000,
      savingsRate: 27,
      transactionCount: 280,
      avgTransactionAmount: 185,
      avgDailySpending: 122,
      topCategory: { name: 'Housing', amount: 8000, percentage: 36 },
      monthOverMonth: { income: 3, expenses: 1, savings: 8 }
    },
    lastRun: new Date(2024, 11, 10),
    isFavorite: false
  }
];



export function SavedReports({ 
  onCreateNew, 
  transactions = [], 
  selectedPeriod,
  mainCurrencySymbol = '$',
  convertToDefault,
  accounts = [],
  defaultCurrency
}: SavedReportsProps) {
  const { categories } = useCategoriesStore();

  // Helper function to get main category name only and filter out uncategorized
  const getMainCategoryName = (transaction: any): string | null => {
    // If no category_id, this is uncategorized - exclude it
    if (!transaction.category_id && !transaction.categoryId) {
      return null;
    }

    const categoryId = transaction.category_id || transaction.categoryId;
    
    // Look up the category in the categories store
    for (const mainCategory of categories) {
      // Check if this is the main category ID
      if (mainCategory.id === categoryId) {
        return mainCategory.name;
      }
      
      // Check subcategories - return main category name
      if (mainCategory.subcategories) {
        const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
        if (subcategory) {
          return mainCategory.name; // Return main category name, not subcategory
        }
      }
    }
    
    // If category not found in store, exclude it
    return null;
  };

  // Helper function to get category color from main category name
  const getCategoryColor = (categoryName: string): string => {
    // Default colors as fallback
    const defaultColors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
      '#8B5CF6', '#06B6D4', '#EC4899', '#F97316',
      '#A855F7', '#14B8A6', '#84CC16', '#6366F1'
    ];

    // Look up the category color from the categories store
    for (const mainCategory of categories) {
      if (mainCategory.name === categoryName) {
        return mainCategory.color || defaultColors[0];
      }
    }
    
    // If category not found, return a default color based on category name hash
    const hash = categoryName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return defaultColors[Math.abs(hash) % defaultColors.length];
  };

  // Helper function to get category icon from main category name
  const getCategoryIcon = (categoryName: string): string => {
    // Look up the category icon from the categories store
    for (const mainCategory of categories) {
      if (mainCategory.name === categoryName) {
        return mainCategory.icon || 'Folder';
      }
    }
    return 'Folder'; // Default icon
  };

  // Category Icon component
  const CategoryIcon = ({ iconName, className, style }: { iconName: string; className?: string; style?: React.CSSProperties }) => {
    // Get the icon component from LucideIcons
    const Icon = (LucideIcons as any)[iconName];
    
    // If the icon doesn't exist, use fallback
    if (!Icon) {
      const FallbackIcon = (LucideIcons as any)['Folder'];
      return <FallbackIcon className={className} style={style} />;
    }
    
    return <Icon className={className} style={style} />;
  };

  // Format currency function for table
  const formatTableCurrency = (amount: number) => {
    return `${mainCurrencySymbol}${amount.toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };
  const [reports, setReports] = useState<SavedReport[]>(MOCK_REPORTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState<SavedReport | null>(null);
  const [filterType, setFilterType] = useState<string>('all');

  // Filter reports based on search and type
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesSearch = report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           report.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = filterType === 'all' || report.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [reports, searchQuery, filterType]);

  // Calculate report data based on current transactions
  const calculateReportData = useMemo(() => {
    if (!selectedReport || !transactions.length) return null;

    // Filter transactions based on report configuration
    let filteredTransactions = transactions.filter(t => {
      // Type filter
      const transactionTypes = selectedReport.filters.transactionTypes || [];
      if (transactionTypes.length > 0 && !transactionTypes.includes(t.type)) return false;
      
      // Amount filter
      const amount = typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount;
      if (selectedReport.filters.minAmount && Math.abs(amount) < selectedReport.filters.minAmount) return false;
      if (selectedReport.filters.maxAmount && Math.abs(amount) > selectedReport.filters.maxAmount) return false;
      
      // Category filter
      const categories = selectedReport.filters.categories || [];
      if (categories.length > 0 && !categories.includes(t.category)) return false;
      
      return true;
    });

    // Calculate metrics with currency conversion
    let totalIncome = 0;
    let totalExpenses = 0;
    const categoryTotals: Record<string, number> = {};

    filteredTransactions.forEach(transaction => {
      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      // Get transaction currency and convert to default currency
      let transactionCurrency = 'USD'; // Default
      try {
        // Find the account for this transaction to get its currency
        const transactionAccount = accounts.find(acc => acc.id === transaction.account_id);
        if (transactionAccount?.currency) {
          transactionCurrency = transactionAccount.currency;
        }
      } catch (error) {
        // Use default currency if account lookup fails
      }

      // Convert to default currency
      const convertedAmount = convertToDefault 
        ? convertToDefault(Math.abs(amount), transactionCurrency)
        : Math.abs(amount);

      if (transaction.type === 'income') {
        totalIncome += convertedAmount;
      } else if (transaction.type === 'expense') {
        totalExpenses += convertedAmount;
        
        const mainCategoryName = getMainCategoryName(transaction);
        // Only include categorized transactions with main categories
        if (mainCategoryName) {
          categoryTotals[mainCategoryName] = (categoryTotals[mainCategoryName] || 0) + convertedAmount;
        }
      }
    });

    const netSavings = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;
    const avgDailySpending = totalExpenses / 30; // Simplified

    const metrics = {
      totalIncome,
      totalExpenses,
      netSavings,
      avgDailySpending,
      savingsRate,
      transactionCount: filteredTransactions.length
    };

    // Category analysis for charts with colors
    const categoryAnalysis = Object.entries(categoryTotals)
      .map(([name, amount]) => ({
        id: name,
        name,
        amount,
        percentage: (amount / totalExpenses) * 100,
        transactionCount: 0, // TODO: Track transaction count per category in saved reports
        color: getCategoryColor(name),
        trend: 0 // TODO: Calculate trend from previous period
      }))
      .sort((a, b) => b.amount - a.amount);

    // Time series data (simplified monthly) with currency conversion
    const monthlyData: Record<string, { income: number; expenses: number }> = {};
    
    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = format(date, 'MMM yyyy');
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { income: 0, expenses: 0 };
      }

      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      // Get transaction currency and convert to default currency
      let transactionCurrency = 'USD'; // Default
      try {
        // Find the account for this transaction to get its currency
        const transactionAccount = accounts.find(acc => acc.id === transaction.account_id);
        if (transactionAccount?.currency) {
          transactionCurrency = transactionAccount.currency;
        }
      } catch (error) {
        // Use default currency if account lookup fails
      }

      // Convert to default currency
      const convertedAmount = convertToDefault 
        ? convertToDefault(Math.abs(amount), transactionCurrency)
        : Math.abs(amount);

      if (transaction.type === 'income') {
        monthlyData[monthKey].income += convertedAmount;
      } else if (transaction.type === 'expense') {
        monthlyData[monthKey].expenses += convertedAmount;
      }
    });

    const timeSeriesData = Object.entries(monthlyData)
      .map(([date, data]) => ({
        date,
        income: data.income,
        expenses: data.expenses,
        savings: data.income - data.expenses,
        balance: data.income - data.expenses
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return { metrics, categoryAnalysis, timeSeriesData };
  }, [selectedReport, transactions]);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `${mainCurrencySymbol}${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${mainCurrencySymbol}${(amount / 1000).toFixed(1)}K`;
    } else {
      return `${mainCurrencySymbol}${amount.toFixed(0)}`;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'category_analysis':
        return <PieChart className="w-4 h-4" />;
      case 'trend_analysis':
        return <TrendingUp className="w-4 h-4" />;
      case 'financial_summary':
        return <BarChart3 className="w-4 h-4" />;
      default:
        return <BarChart3 className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'category_analysis':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800';
      case 'trend_analysis':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800';
      case 'financial_summary':
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800';
    }
  };

  const handleViewReport = (report: SavedReport) => {
    setSelectedReport(report);
  };

  const handleDeleteReport = async (reportId: string) => {
    try {
      setReports(prev => prev.filter(r => r.id !== reportId));
      toast.success('Report deleted successfully');
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleExportReport = async (report: SavedReport, format: 'pdf' | 'excel') => {
    try {
      // Calculate real-time data for export
      const exportData = calculateReportData;
      if (!exportData) {
        toast.error('No data available for export');
        return;
      }

      // Convert SavedReport and data to the format expected by reportGenerator
      const reportForExport: Report = {
        id: report.id,
        name: report.name,
        type: (() => {
          switch (report.type) {
            case 'category_breakdown':
              return 'category' as const;
            case 'trend_analysis':
              return 'trend' as const;
            case 'comparison':
              return 'comparison' as const;
            default:
              return 'category' as const;
          }
        })(),
        chartType: (() => {
          switch (report.chartType) {
            case 'doughnut':
              return 'donut' as const;
            default:
              return report.chartType as 'pie' | 'line' | 'bar' | 'area';
          }
        })(),
        category: report.categories?.[0],
        dateRange: {
          from: report.period.startDate,
          to: report.period.endDate
        },
        description: report.description,
        createdAt: report.createdAt.toISOString(),
        data: {
          transactions: [],
          totalIncome: exportData.metrics.totalIncome || 0,
          totalExpenses: exportData.metrics.totalExpenses || 0,
          netIncome: exportData.metrics.netSavings || 0,
          netBalance: exportData.metrics.netSavings || 0,
          chartData: exportData.categoryAnalysis?.map(cat => ({
            name: cat.name,
            value: cat.amount,
            income: 0,
            expenses: cat.amount,
            net: -cat.amount
          })) || exportData.timeSeriesData?.map(item => ({
            name: item.date,
            value: item.expenses,
            income: item.income,
            expenses: item.expenses,
            net: item.savings
          })) || [],
          originalAmounts: {}
        }
      };

      if (format === 'pdf') {
        const pdf = generatePDFReport(reportForExport);
        pdf.save(`${report.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_report.pdf`);
        toast.success('PDF report downloaded successfully');
      } else {
        const workbook = generateExcelReport(reportForExport);
        XLSX.writeFile(workbook, `${report.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_report.xlsx`);
        toast.success('Excel report downloaded successfully');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export report as ${format.toUpperCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (selectedReport && calculateReportData) {
    return (
      <div className="space-y-6">
        {/* Report Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="outline"
              onClick={() => setSelectedReport(null)}
              className="mb-4 bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
            >
              ← Back to Reports
            </Button>
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
                {selectedReport.name}
              </h2>
              {selectedReport.isFavorite && (
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
              )}
            </div>
            <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
              {selectedReport.description}
            </p>
            <div className="flex items-center space-x-4 mt-2">
              <Badge variant="secondary" className={getTypeColor(selectedReport.type)}>
                {getTypeIcon(selectedReport.type)}
                <span className="ml-1 capitalize">{selectedReport.type.replace('_', ' ')}</span>
              </Badge>
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Period: {selectedPeriod?.label || 'Custom'}
              </span>
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Last run: {format(selectedReport.lastRun, 'MMM dd, yyyy')}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => handleExportReport(selectedReport, 'excel')}
              className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportReport(selectedReport, 'pdf')}
              className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Report Content */}
        {calculateReportData && (
          <div className="space-y-6">
            {/* Metrics */}
            {selectedReport.includeMetrics.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selectedReport.includeMetrics.map((metricId) => {
                  const value = calculateReportData.metrics[metricId as keyof typeof calculateReportData.metrics];
                  
                  return (
                    <MetricCard
                      key={metricId}
                      title={metricId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      value={
                        metricId === 'savingsRate' 
                          ? `${(value as number).toFixed(1)}%`
                          : formatCurrency(value as number)
                      }
                      icon={getTypeIcon(selectedReport.type)}
                      iconColor="text-primary dark:text-primary-dark"
                      iconBgColor="bg-primary/10 dark:bg-primary-dark/10"
                    />
                  );
                })}
              </div>
            )}

            {/* Chart */}
            <div className="mt-6">
              {selectedReport.type === 'category_breakdown' || selectedReport.chartType === 'pie' || selectedReport.chartType === 'doughnut' ? (
                <div className="space-y-6">
                  <Chart
                    type={selectedReport.chartType}
                    data={calculateReportData.categoryAnalysis}
                    title={selectedReport.name}
                    subtitle={selectedReport.description}
                    height={400}
                    mainCurrencySymbol={mainCurrencySymbol}
                    mainCurrencyCode={defaultCurrency}
                  />
                  
                  {/* Category Breakdown Table */}
                  <Card className="border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                          Category Breakdown
                        </h3>
                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 dark:bg-primary-dark/10 dark:text-primary-dark dark:border-primary-dark/20">
                          {calculateReportData.categoryAnalysis.length} Categories
                        </Badge>
                      </div>
                      
                      <div className="rounded-xl border border-border dark:border-border-dark overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-background-primary/50 dark:bg-background-dark-primary/50">
                              <TableHead className="font-semibold text-text-primary dark:text-text-dark-primary">Category</TableHead>
                              <TableHead className="font-semibold text-text-primary dark:text-text-dark-primary text-right">Amount</TableHead>
                              <TableHead className="font-semibold text-text-primary dark:text-text-dark-primary text-right">Percentage</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {calculateReportData.categoryAnalysis.map((category, index) => (
                              <TableRow 
                                key={category.id} 
                                className={cn(
                                  "transition-colors hover:bg-background-primary/30 dark:hover:bg-background-dark-primary/30",
                                  index % 2 === 0 ? "bg-background-secondary/20 dark:bg-background-dark-secondary/20" : ""
                                )}
                              >
                                <TableCell className="py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                      {/* Category Color Indicator */}
                                      <div
                                        className="w-4 h-4 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: category.color }}
                                      />
                                      {/* Category Icon */}
                                      <div 
                                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ 
                                          backgroundColor: `${category.color}15`,
                                          border: `1px solid ${category.color}25`
                                        }}
                                      >
                                        <CategoryIcon 
                                          iconName={getCategoryIcon(category.name)} 
                                          className="w-4 h-4" 
                                          style={{ color: category.color }} 
                                        />
                                      </div>
                                    </div>
                                    {/* Category Name */}
                                    <div className="flex flex-col">
                                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                                        {category.name}
                                      </span>
                                      <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                                        {category.transactionCount || 0} transactions
                                      </span>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right py-4">
                                  <span className="font-semibold text-text-primary dark:text-text-dark-primary">
                                    {formatTableCurrency(category.amount)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right py-4">
                                  <div className="flex items-center justify-end gap-2">
                                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                                      {category.percentage.toFixed(1)}%
                                    </span>
                                    {/* Percentage Bar */}
                                    <div className="w-16 h-2 bg-background-primary/30 dark:bg-background-dark-primary/30 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full rounded-full transition-all duration-300"
                                        style={{ 
                                          width: `${category.percentage}%`,
                                          backgroundColor: category.color
                                        }}
                                      />
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      
                      {/* Summary */}
                      {calculateReportData.categoryAnalysis.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border dark:border-border-dark">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-text-secondary dark:text-text-dark-secondary">
                              Total Categories: {calculateReportData.categoryAnalysis.length}
                            </span>
                            <span className="font-semibold text-text-primary dark:text-text-dark-primary">
                              Total: {formatTableCurrency(calculateReportData.categoryAnalysis.reduce((sum, cat) => sum + cat.amount, 0))}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              ) : (
                <Chart
                  type={selectedReport.chartType}
                  data={calculateReportData.timeSeriesData}
                  title={selectedReport.name}
                  subtitle={selectedReport.description}
                  height={400}
                  mainCurrencySymbol={mainCurrencySymbol}
                  mainCurrencyCode={defaultCurrency}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
            Saved Reports
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
            Manage and run your custom analytics reports
          </p>
        </div>
        
        <Button onClick={onCreateNew} className="bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90">
          <Plus className="w-4 h-4 mr-2" />
          Create Report
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
          <Input
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark"
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterType('all')}
            size="sm"
            className={cn(
              filterType === 'all' 
                ? "bg-primary text-white dark:bg-primary-dark" 
                : "bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary"
            )}
          >
            All
          </Button>
          <Button
            variant={filterType === 'income_expense' ? 'default' : 'outline'}
            onClick={() => setFilterType('income_expense')}
            size="sm"
            className={cn(
              filterType === 'income_expense' 
                ? "bg-primary text-white dark:bg-primary-dark" 
                : "bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary"
            )}
          >
            Summary
          </Button>
          <Button
            variant={filterType === 'category_breakdown' ? 'default' : 'outline'}
            onClick={() => setFilterType('category_breakdown')}
            size="sm"
            className={cn(
              filterType === 'category_breakdown' 
                ? "bg-primary text-white dark:bg-primary-dark" 
                : "bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary"
            )}
          >
            Categories
          </Button>
          <Button
            variant={filterType === 'trend_analysis' ? 'default' : 'outline'}
            onClick={() => setFilterType('trend_analysis')}
            size="sm"
            className={cn(
              filterType === 'trend_analysis' 
                ? "bg-primary text-white dark:bg-primary-dark" 
                : "bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary"
            )}
          >
            Trends
          </Button>
        </div>
      </div>

      {/* Reports Grid */}
      {filteredReports.length === 0 ? (
        <Card className="p-12 text-center bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark">
          <div className="text-text-secondary dark:text-text-dark-secondary mb-4">
            📊 No reports found
          </div>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
            {searchQuery ? 'Try adjusting your search query.' : 'Create your first analytics report to get started.'}
          </p>
          <Button onClick={onCreateNew} className="bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90">
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Report
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReports.map((report) => (
            <Card key={report.id} className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary" className={getTypeColor(report.type)}>
                      {getTypeIcon(report.type)}
                      <span className="ml-1 capitalize">{report.type.replace('_', ' ')}</span>
                    </Badge>
                    {report.isFavorite && (
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    )}
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark">
                      <DropdownMenuItem onClick={() => handleViewReport(report)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Report
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportReport(report, 'excel')}>
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportReport(report, 'pdf')}>
                        <Download className="w-4 h-4 mr-2" />
                        Export PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteReport(report.id)}
                        className="text-negative dark:text-negative-dark"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <h3 className="font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                  {report.name}
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4 line-clamp-2">
                  {report.description}
                </p>
                
                <div className="flex items-center justify-between text-xs text-text-secondary dark:text-text-dark-secondary mb-4">
                  <span>Created {format(report.createdAt, 'MMM dd, yyyy')}</span>
                  <span>Last run {format(report.lastRun, 'MMM dd')}</span>
                </div>
                
                <Button 
                  onClick={() => handleViewReport(report)}
                  className="w-full bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                  size="sm"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Run Report
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}


    </div>
  );
} 